// ── Action Center ────────────────────────────────────────────────────────────
//
// The Dashboard is the shape of the book. This page is the to-do list: every
// card here is a pile somebody is meant to work —
//
//   SERVICING              Unposted sweeps · Owed to participants ·
//                          Unfunded draws · ACH not set up
//   PORTFOLIO MANAGEMENT   Overdue covenants · UCC Filings · UCC Searches ·
//                          Tax monitoring · News · MCA monitoring · Bank activity
//
// The cards are the Dashboard's own components (exported from DashboardPage),
// fed by the same endpoints, so a number here and the page it links to cannot
// disagree. UCC and tax are for portfolio managers and admins only, as before;
// MCA and bank activity need the bank-feed permission, as on their own pages.
//
// Portfolio managers land here when they sign in; everyone else lands on the
// Dashboard. The sidebar badge and the Dashboard's one-line strip are the
// count below (actionCounts), computed by the same code from the same data.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Card, ErrorMsg, PageLink, Spinner } from "../../../shared/ui";
import { isDrawFunded } from "../../../shared/drawStatus";
import { useApp } from "../../../shared/appContext";
import { UnpostedSweeps, OwedToParticipants, OpenItemsTile, OverdueCovenants, UccMonitoring, TaxMonitoring,
         daysSince, UCC_LIVE, UCC_KINDS } from "./DashboardPage";

const cardTitle = { fontSize:11, fontWeight:700, color:B.cyanDark, textTransform:"uppercase", letterSpacing:"0.06em",
                    marginBottom:10, paddingBottom:6, borderBottom:"1.5px solid "+B.cyanLight };

// Everything the cards need. Each optional source has its own catch, so one
// register not being set up degrades its card and nothing else (the rule the
// Dashboard always followed).
export async function loadActionData(api, role) {
  const canMon = !!(role && role.canViewMonitoring);
  const canBank = !!(role && role.canViewBankFeeds);
  const [dash, draws, accounts, wftx, achCol, tax, ucc, mca, bank, mon, news] = await Promise.all([
    api.get("getDashboard"),
    api.get("getDraws").catch(() => []),
    api.get("getAccounts").catch(() => []),
    api.get("getWFTransactions", { status: "unposted" }).catch(() => []),
    api.get("getACHCollections").catch(() => []),
    canMon ? api.get("getTaxMonitoring").catch(() => null) : Promise.resolve(null),
    canMon ? Promise.all([api.get("getUCCFilings"), api.get("getCSCOrders").catch(() => null),
                          api.get("getUCCSearches").catch(() => null)])
               .then(([u, c, s]) => ({ rows: (u && u.rows) || [], csc: c && c.counts ? c.counts : null,
                                       searches: s ? { counts: s.counts || {}, red: (s.borrowers || []).filter(b => b.red && b.live).length,
                                                       acknowledged: s.acknowledged || [] } : null }))
               .catch(() => null)
           : Promise.resolve(null),
    // Open MCA findings and open bank-activity alerts — each page's default view.
    canBank ? api.get("getMCAFindings").then(x => (x && x.findings) || []).catch(() => null) : Promise.resolve(null),
    canBank ? api.get("getBankAlerts").then(x => (x && x.alerts) || []).catch(() => null) : Promise.resolve(null),
    // The sidebar's own badge counts — UCC Searches is read from here so the
    // two numbers are the same number (searches to tie out + tracking alerts).
    api.get("getMonitoringCounts").catch(() => null),
    // Open News Monitor stories (null when the tables are not set up).
    // Low (no trouble word) stays on News Monitor; the card is for High / Medium.
    canMon ? api.get("getNewsFindings").then(x => ((x && x.findings) || []).filter(f => f.severity !== "Low")).catch(() => null) : Promise.resolve(null),
  ]);
  return { dash: dash || {}, draws: Array.isArray(draws) ? draws : [], accounts: Array.isArray(accounts) ? accounts : [],
           wftx: Array.isArray(wftx) ? wftx : [], achCol: Array.isArray(achCol) ? achCol : [], tax, ucc, canMon,
           mca, bank, canBank, mon, news };
}

// The piles, as the cards count them.
const unfundedOf = d => d.draws.filter(x => !isDrawFunded(x));
const achNotSetUpOf = d => d.achCol.filter(a => { const v = a.ach_collection_set_up_date; return v == null || String(v).trim() === ""; });
// "Waiting" the way the tiles split it: past its date, or undated.
const waiting = (rows, dateOf) => rows.filter(r => { const n = daysSince(dateOf(r)); return n === null || n >= 0; }).length;

// What needs somebody now — the Action Center badge.
//
// ONE NUMBER PER PILE. Wherever a pile has a sidebar badge, its part here IS
// that badge (getMonitoringCounts): Pmt Posting, Covenant Calendar, UCC
// Filings, UCC Searches, Tax Monitoring, MCA Monitor, Bank Activity. So the
// Action Center badge is the sidebar badges added up, plus the piles that have
// no sidebar page (owed to participants, unfunded draws, ACH not set up). The
// card computations below are only the fallback when the counts call fails. Items merely
// scheduled ahead (a draw due next week, payments due to participants in the
// next 30 days) are on the cards but not in this count.
export function actionCounts(d) {
  if (!d) return null;
  const parts = {
    sweeps: d.wftx.length,
    participants: Number((d.dash.participantOwed || {}).owedcnt) || 0,
    draws: waiting(unfundedOf(d), x => x.expectedfunddate),
    ach: waiting(achNotSetUpOf(d), a => a.requested_ach_date),
    // Red only (more than a week late) — the same number as the Covenant
    // Calendar bubble. The card still shows every overdue entry for context.
    covenants: Number((d.dash.covenantOverdue || {}).red) || 0,
  };
  if (d.canMon) {
    if (d.ucc) {
      const live = (d.ucc.rows || []).filter(v => v.status !== "Terminated" && UCC_LIVE.includes(v.accountstatus));
      const flagged = live.filter(v => UCC_KINDS.some(k => k.test(v))).length;
      const covered = new Set(live.filter(v => v.status !== "LAPSED").map(v => String(v.accountid)));
      const reasons = new Set(((d.ucc.searches && d.ucc.searches.acknowledged) || []).map(x => String(x.accountid)));
      const uncovered = d.accounts.filter(a => UCC_LIVE.includes(a.accountstatus)
        && !covered.has(String(a.id)) && !reasons.has(String(a.id))).length;
      parts.ucc = flagged + uncovered;
    }
    if (d.tax) parts.tax = (Array.isArray(d.tax.worklist) ? d.tax.worklist : []).filter(w => !w.acked_at).length;
    // UCC Searches: searches not yet tied out to a borrower, plus open Debtor
    // Tracking alerts — exactly the sidebar badge on UCC Searches.
    if (d.mon && d.mon.searches != null) parts.searches = Number(d.mon.searches) || 0;
    if (d.news) parts.news = d.news.filter(f => f.status === "Open").length;
  }
  if (d.canBank) {
    if (d.mca) parts.mca = d.mca.filter(f => f.status === "Open").length;
    if (d.bank) parts.bank = d.bank.filter(a => a.status === "Open").length;
  }
  const m = d.mon || {};
  const take = (key, monKey) => { if (m[monKey] != null) parts[key] = Number(m[monKey]) || 0; };
  take("sweeps", "pmtposting");
  take("covenants", "covenants");
  if (d.canMon) { take("ucc", "ucc"); take("searches", "searches"); take("tax", "tax"); take("news", "news"); }
  if (d.canBank) { take("mca", "mca"); take("bank", "bank"); }
  return { total: Object.values(parts).reduce((a, n) => a + (n || 0), 0), parts };
}

// ── MCA monitoring and Bank activity: open items, worst first ──
// Same reading as the account page's cards: High in red, the rest amber, the
// borrower beside each, and the page that works them one click away.
const RED = "#993C1D", AMBER = "#7A5200";
const MonitorCard = ({ heading, page, cta, items, sevOf, labelOf, calm }) => {
  const shell = { padding:16, height:"100%", boxSizing:"border-box" };
  const link = <PageLink page={page} style={{ display:"inline-block", marginTop:12, fontSize:12, fontWeight:600, color:B.cyanDark }}>
    {cta} &rarr;</PageLink>;
  if (items === null) {
    return <Card style={shell}>
      <div style={cardTitle}>{heading}</div>
      <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>Not reporting. The tables may not be set up yet.</div>
      {link}
    </Card>;
  }
  const open = items.filter(x => x.status === "Open");
  const high = open.filter(x => sevOf(x) === "High").length;
  const accts = new Set(open.map(x => x.accountid)).size;
  const sub = { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em",
                color:"var(--color-text-secondary)", marginBottom:4 };
  // Same shape as the other cards: the count, its caption and the link on the
  // left; what makes it up on the right.
  return <Card style={shell}>
    <div style={cardTitle}>{heading}</div>
    {!open.length ? <><div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>{calm}</div>{link}</> :
    <div style={{ display:"grid", gap:16, gridTemplateColumns:"minmax(0, 1fr) minmax(0, 2fr)" }}>
      <div>
        <div style={{ fontSize:30, fontWeight:600, lineHeight:1 }}>{open.length}</div>
        <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginTop:6 }}>
          open across {accts} account{accts === 1 ? "" : "s"}</div>
        {high > 0 && <div style={{ fontSize:13, fontWeight:600, color:RED, marginTop:4 }}>{high} high</div>}
        {link}
      </div>
      <div>
        <div style={sub}>What needs doing</div>
        {open.slice(0, 5).map((x, i) => <div key={x.id || i} style={{ display:"flex", gap:8, fontSize:12, padding:"4px 0",
              borderTop:"0.5px solid var(--color-border-tertiary)" }}>
          <span style={{ width:8, height:8, borderRadius:4, marginTop:4, flexShrink:0,
                         background: sevOf(x) === "High" ? RED : AMBER }} />
          <span style={{ minWidth:0 }}>
            <span style={{ fontWeight:500 }}>{x.companyname || "—"}</span>
            <div style={{ color:"var(--color-text-secondary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{labelOf(x)}</div>
          </span>
        </div>)}
        {open.length > 5 && <div style={{ fontSize:11.5, color:"var(--color-text-secondary)", marginTop:4 }}>and {open.length - 5} more</div>}
      </div>
    </div>}
  </Card>;
};

// ── UCC Searches ──
// The big number is the sidebar badge, read from the same count: searches not
// yet tied out to a borrower plus open Debtor Tracking alerts — the to-do.
// Below it, what the searches SAY (red findings, borrowers not searched in a
// year): shown for context and worked on the page, but not part of the count —
// a finding is cleared by fixing it or marking it known, not by a queue.
const UccSearchesCard = ({ searches, mon }) => {
  const shell = { padding:16, height:"100%", boxSizing:"border-box" };
  const link = <PageLink page="ucc-searches" style={{ display:"inline-block", marginTop:12, fontSize:12, fontWeight:600, color:B.cyanDark }}>
    Open UCC Searches &rarr;</PageLink>;
  if (!mon && !searches) return <Card style={shell}><div style={cardTitle}>UCC Searches</div>
    <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>Not reporting. The search tables may not be set up yet.</div>{link}</Card>;
  const total = mon ? Number(mon.searches) || 0 : 0;
  const tie = mon ? Number(mon.searchesReview) || 0 : 0;
  const alerts = mon ? Number(mon.trackingAlerts) || 0 : 0;
  const c = (searches && searches.counts) || {}, red = (searches && searches.red) || 0;
  const line = (label, n, tone) => <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, padding:"4px 0",
                                                 borderTop:"0.5px solid var(--color-border-tertiary)" }}>
    <span>{label}</span><span style={{ fontWeight:600, fontVariantNumeric:"tabular-nums", color: n ? tone : undefined }}>{n}</span></div>;
  const sub = { fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--color-text-secondary)",
                margin:"14px 0 4px" };
  // Same shape as the other cards: the count and its caption on the left,
  // what makes it up (and the context that doesn't count) on the right.
  return <Card style={shell}>
    <div style={cardTitle}>UCC Searches</div>
    <div style={{ display:"grid", gap:16, gridTemplateColumns:"repeat(2, minmax(0, 1fr))" }}>
      <div>
        {total ? <>
            <div style={{ fontSize:30, fontWeight:600, lineHeight:1 }}>{total}</div>
            <div style={{ fontSize:13, color:"var(--color-text-secondary)", marginTop:6 }}>
              search{total === 1 ? "" : "es"} and alert{total === 1 ? "" : "s"} waiting</div>
          </>
          : <div style={{ fontSize:13, color:"var(--color-text-secondary)" }}>
              Nothing needs attention — no searches to tie out and no open tracking alerts.</div>}
        {link}
      </div>
      <div>
        <div style={{ ...sub, marginTop:0 }}>What needs doing</div>
        {line("Searches to tie out to a borrower", tie, AMBER)}
        {line("Debtor Tracking alerts", alerts, RED)}
        <div style={sub}>What the searches say <span style={{ textTransform:"none", letterSpacing:0, fontWeight:400 }}>· not in the count</span></div>
        {line("Live borrowers with a red finding", red, RED)}
        {line("Open findings on latest searches", c.findings || 0, AMBER)}
        {line("Not searched in 12 months", c.stale || 0, AMBER)}
      </div>
    </div>
  </Card>;
};

// A section: one card holding that section's cards, titled in the page's band style.
// `cols` fixes the number per row (the cards wrap onto more rows, so the
// section grows taller instead of each card getting narrower); otherwise as
// many as fit at `min` px.
const Section = ({ title, children, min, cols }) => (
  <Card style={{ padding:16, marginBottom:16 }}>
    <div style={{ fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.06em", color:B.cyanDark,
                  padding:"8px 12px", marginBottom:14, borderRadius:6, background:B.cyanLight }}>{title}</div>
    <div style={{ display:"grid", gridTemplateColumns: cols ? `repeat(${cols}, minmax(0, 1fr))` : `repeat(auto-fit, minmax(${min}px, 1fr))`, gap:16, alignItems:"stretch" }}>
      {children}
    </div>
  </Card>
);

export const ActionCenterPage = () => {
  const { api, role, setActionCount } = useApp();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => {
    setErr(null);
    loadActionData(api, role)
      .then(x => { setD(x); const c = actionCounts(x); if (setActionCount) setActionCount(c ? c.total : null); })
      .catch(e => setErr(e.message));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorMsg msg={err} onRetry={load} />;
  if (!d) return <Spinner />;

  const wftx = d.wftx;
  const unmapped = wftx.filter(t => {
    const lr = t.loanreference == null ? "" : String(t.loanreference).trim().toLowerCase();
    return lr === "" || lr === "none" || lr === "null";
  });

  return <>

    {/* ── Servicing ── */}
    <Section title="Servicing" min={240}>
      <UnpostedSweeps count={wftx.length}
                      total={wftx.reduce((s, t) => s + (parseFloat(t.transactionamount) || 0), 0)}
                      unmapped={unmapped.length} mapped={wftx.length - unmapped.length} title={cardTitle} />
      <OwedToParticipants p={d.dash.participantOwed || {}} title={cardTitle} />
      <OpenItemsTile
        page="all-draws" heading="Unfunded draws" title={cardTitle} unit="draw"
        rows={unfundedOf(d)}
        amountOf={x => x.expecteddrawamount}
        dateOf={x => x.expectedfunddate}
        lateLabel="past their fund date"
        aheadLabel="scheduled ahead"
        cta="Open draws" calm="No unfunded draws." />
      <OpenItemsTile
        page="all-achcollections" heading="ACH not set up" title={cardTitle} unit="collection"
        rows={achNotSetUpOf(d)}
        amountOf={a => a.ach_requested_amt}
        dateOf={a => a.requested_ach_date}
        lateLabel="requested and waiting"
        aheadLabel="requested for later"
        cta="Set them up" calm="Nothing awaiting set up." />
    </Section>

    {/* ── Portfolio management: covenants and the monitoring registers ── */}
    <Section title="Portfolio management" cols={3}>
      <OverdueCovenants co={d.dash.covenantOverdue} title={cardTitle} compact />
      {d.canMon && <UccMonitoring ucc={d.ucc} accounts={d.accounts} title={cardTitle} />}
      {d.canMon && <UccSearchesCard searches={d.ucc && d.ucc.searches} mon={d.mon} />}
      {d.canMon && <TaxMonitoring tax={d.tax} title={cardTitle} />}
      {d.canMon && <MonitorCard heading="Negative news" page="news-monitor" cta="Open News Monitor" items={d.news}
          sevOf={f => f.severity} labelOf={f => f.title || ""} calm="No open news stories." />}
      {d.canBank && <MonitorCard heading="MCA monitoring" page="mca-monitor" cta="Open MCA Monitor" items={d.mca}
          sevOf={f => f.confidence} labelOf={f => f.mcaname || f.label || ""} calm="No open MCA findings." />}
      {d.canBank && <MonitorCard heading="Bank activity" page="bank-activity" cta="Open Bank Activity" items={d.bank}
          sevOf={a => a.severity} labelOf={a => a.title || a.rulekey || ""} calm="No open bank activity alerts." />}
    </Section>
  </>;
};
